'use client';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { extractVideoId } from '@/helpers/extractVideoId';
import { RotateCcw, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface YouTubeVideoPlayerProps {
    videoUrl: string;
    title?: string;
    className?: string;
    showTitle?: boolean;
}

export default function YouTubeVideoPlayer({
    videoUrl,
    title = 'Video',
    className = '',
    showTitle = false,
}: YouTubeVideoPlayerProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showRotationHint, setShowRotationHint] = useState(false);

    const videoId = extractVideoId(videoUrl);

    const handlePlay = () => {
        setIsModalOpen(true);
    };

    // Detect orientation and show hint on mobile portrait
    useEffect(() => {
        const checkOrientation = () => {
            const portrait = window.innerHeight > window.innerWidth;
            const isMobile = window.innerWidth < 640; // sm breakpoint

            // Show rotation hint for mobile portrait users after opening video
            if (isModalOpen && portrait && isMobile) {
                setShowRotationHint(true);
                // Auto-hide hint after 4 seconds
                const timer = setTimeout(() => setShowRotationHint(false), 4000);
                return () => clearTimeout(timer);
            }
            setShowRotationHint(false);
        };

        checkOrientation();
        window.addEventListener('resize', checkOrientation);
        window.addEventListener('orientationchange', checkOrientation);

        return () => {
            window.removeEventListener('resize', checkOrientation);
            window.removeEventListener('orientationchange', checkOrientation);
        };
    }, [isModalOpen]);

    if (!videoId) {
        return null;
    }

    return (
        <div className={className}>
            <button
                type="button"
                className="group relative w-full cursor-pointer"
                onClick={handlePlay}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handlePlay();
                    }
                }}
                aria-label={`Play video: ${title}`}
            >
                <div className="aspect-video w-full overflow-hidden rounded-lg bg-haug-choco-70">
                    <img
                        src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                        alt={title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                            // Fallback to standard quality thumbnail if maxres fails
                            const target = e.target as HTMLImageElement;
                            target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                        }}
                    />

                    {/* Play button overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 transition-opacity duration-300 group-hover:bg-opacity-40">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600 transition-transform duration-300 group-hover:scale-110">
                            <svg
                                className="h-6 w-6 fill-white"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                            >
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </button>

            {showTitle && title && (
                <div className="mt-4 px-2">
                    <h3 className="font-neue-haas font-semibold text-haug-choco-100 text-lg">
                        {title}
                    </h3>
                </div>
            )}

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="flex w-[95vw] max-w-6xl flex-col p-0 sm:h-[95vh] sm:max-h-[800px]">
                    <DialogHeader className="sr-only">
                        <DialogTitle>{title}</DialogTitle>
                    </DialogHeader>
                    <div className="relative aspect-video w-full sm:flex-1">
                        <iframe
                            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                            title={title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="h-full w-full rounded-lg"
                        />

                        {/* Rotation hint overlay for mobile portrait */}
                        {showRotationHint && (
                            <div className="absolute inset-0 z-40 flex items-center justify-center bg-black bg-opacity-60 font-neue-haas">
                                <div className="max-w-xs p-6 text-center text-white">
                                    <RotateCcw className="mx-auto mb-3 h-8 w-8 animate-pulse" />
                                    <p className="font-neue-haas text-sm">
                                        Rotate your device for a better viewing experience
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setShowRotationHint(false)}
                                        className="mt-3 text-white text-xs underline opacity-75 hover:opacity-100"
                                    >
                                        Continue anyway
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Close button - always visible, higher z-index to appear above iframe */}
                        <button
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="absolute top-2 right-2 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-black bg-opacity-80 text-white shadow-lg transition-all duration-200 hover:bg-opacity-90"
                            aria-label="Close video"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
